import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cx } from '@/lib/cx';
import {
  addDaysISO,
  addMonthsISO,
  calendarHeading,
  dayLabel,
  dayOfWeek,
  daysInMonth,
  isWithinRange,
  monthMatrix,
  splitISO,
  warekiYearOfYear,
  type DateKind,
} from '@/lib/dateInput';
import { formatISO, todayISO } from '@/lib/wareki';

type View = 'day' | 'month' | 'year';

export interface DateCalendarProps {
  /** ポップアップの id（入力欄の aria-controls から参照） */
  id: string;
  kind: DateKind;
  /** 現在の値（ISO。空なら ''） */
  value: string;
  min?: string;
  max?: string;
  /** 位置合わせの基準（入力欄の枠） */
  anchorRef: RefObject<HTMLElement>;
  /** ポップアップのルート要素（外側クリック判定用に親へ渡す） */
  popupRef: RefObject<HTMLDivElement>;
  /** 開いた直後にカレンダー（日付グリッド）へフォーカスを移す */
  autoFocus?: boolean;
  /** 日付（month 種別は年月）を選択した */
  onSelect: (value: string) => void;
  onClear: () => void;
  /** 閉じる（Esc）。入力欄へフォーカスを戻す */
  onClose: () => void;
}

const WEEK = ['日', '月', '火', '水', '木', '金', '土'] as const;
const GAP = 4;
const MARGIN = 8;

function ym(iso: string): string {
  return iso.slice(0, 7);
}

/** 初期カーソル：値 → 今日 → 範囲の端 */
function initialCursor(kind: DateKind, value: string, min?: string, max?: string): string {
  let c = value ? (kind === 'month' ? `${value}-01` : value) : todayISO();
  const lo = min ? (kind === 'month' ? `${min}-01` : min) : undefined;
  const hi = max ? (kind === 'month' ? `${max}-01` : max) : undefined;
  if (lo && c < lo) c = lo;
  if (hi && c > hi) c = hi;
  return c;
}

/**
 * 独自 DatePicker のポップアップカレンダー（DateField の内部部品）。
 * body 直下へポータル描画し、入力欄の下（入らなければ上）に position: fixed で配置する。
 */
export function DateCalendar({ id, kind, value, min, max, anchorRef, popupRef, autoFocus = true, onSelect, onClear, onClose }: DateCalendarProps) {
  const headingId = useId();
  const [view, setView] = useState<View>(kind === 'month' ? 'month' : 'day');
  const [cursor, setCursor] = useState(() => initialCursor(kind, value, min, max));
  const [pos, setPos] = useState<{ top: number; left: number; placement: 'below' | 'above' } | null>(null);
  const gridRef = useRef<HTMLTableElement>(null);
  /** 次の描画後にアクティブセルへフォーカスする */
  const wantFocus = useRef(autoFocus);

  const today = todayISO();
  const { y, m } = splitISO(cursor);

  // ---- 位置計算（下に収まらなければ上、左右は画面内に収める）
  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current;
      const pop = popupRef.current;
      if (!anchor || !pop) return;
      const r = anchor.getBoundingClientRect();
      const w = pop.offsetWidth;
      const h = pop.offsetHeight;
      const vw = document.documentElement.clientWidth || window.innerWidth;
      const vh = window.innerHeight;
      const spaceBelow = vh - r.bottom - GAP - MARGIN;
      const spaceAbove = r.top - GAP - MARGIN;
      const placement = h <= spaceBelow || spaceBelow >= spaceAbove ? 'below' : 'above';
      let top = placement === 'below' ? r.bottom + GAP : r.top - GAP - h;
      top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
      const left = Math.max(MARGIN, Math.min(r.left, vw - w - MARGIN));
      setPos((p) => (p && p.top === top && p.left === left && p.placement === placement ? p : { top, left, placement }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, popupRef, view]);

  // ---- アクティブセルへフォーカス（キーボード操作・開いた直後）
  // 位置が決まる（visibility: hidden が外れる）までは focus できないため待つ
  useEffect(() => {
    if (!pos || !wantFocus.current) return;
    wantFocus.current = false;
    gridRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.focus();
  });

  const isDisabledDay = (iso: string) => !isWithinRange(iso, min, max);
  const monthOf = (yy: number, mm: number) => formatISO({ y: yy, m: mm, d: 1 }).slice(0, 7);
  /** 年月がまるごと範囲外か */
  const isDisabledMonth = (yy: number, mm: number) => {
    if (kind === 'month') return !isWithinRange(monthOf(yy, mm), min, max);
    const first = formatISO({ y: yy, m: mm, d: 1 });
    const last = formatISO({ y: yy, m: mm, d: daysInMonth(yy, mm) });
    return (!!max && first > max) || (!!min && last < min);
  };
  const isDisabledYear = (yy: number) => {
    const lo = min?.slice(0, 4);
    const hi = max?.slice(0, 4);
    const ys = String(yy).padStart(4, '0');
    return (!!lo && ys < lo) || (!!hi && ys > hi);
  };

  const move = (next: string, focus = true) => {
    wantFocus.current = focus;
    setCursor(next);
  };

  const selectDay = (iso: string) => {
    if (isDisabledDay(iso)) return;
    onSelect(iso);
  };
  const selectMonth = (yy: number, mm: number) => {
    if (isDisabledMonth(yy, mm)) return;
    if (kind === 'month') {
      onSelect(monthOf(yy, mm));
      return;
    }
    wantFocus.current = true;
    setCursor(formatISO({ y: yy, m: mm, d: Math.min(splitISO(cursor).d, daysInMonth(yy, mm)) }));
    setView('day');
  };
  const selectYear = (yy: number) => {
    if (isDisabledYear(yy)) return;
    wantFocus.current = true;
    setCursor(formatISO({ y: yy, m, d: Math.min(splitISO(cursor).d, daysInMonth(yy, m)) }));
    setView('month');
  };

  const onGridKeyDown = (e: KeyboardEvent<HTMLTableElement>) => {
    const k = e.key;
    let handled = true;
    if (view === 'day') {
      if (k === 'ArrowLeft') move(addDaysISO(cursor, -1));
      else if (k === 'ArrowRight') move(addDaysISO(cursor, 1));
      else if (k === 'ArrowUp') move(addDaysISO(cursor, -7));
      else if (k === 'ArrowDown') move(addDaysISO(cursor, 7));
      else if (k === 'Home') move(addDaysISO(cursor, -dayOfWeek(cursor)));
      else if (k === 'End') move(addDaysISO(cursor, 6 - dayOfWeek(cursor)));
      else if (k === 'PageUp') move(addMonthsISO(cursor, e.shiftKey ? -12 : -1));
      else if (k === 'PageDown') move(addMonthsISO(cursor, e.shiftKey ? 12 : 1));
      else if (k === 'Enter' || k === ' ') selectDay(cursor);
      else handled = false;
    } else if (view === 'month') {
      if (k === 'ArrowLeft') move(addMonthsISO(cursor, -1));
      else if (k === 'ArrowRight') move(addMonthsISO(cursor, 1));
      else if (k === 'ArrowUp') move(addMonthsISO(cursor, -3));
      else if (k === 'ArrowDown') move(addMonthsISO(cursor, 3));
      else if (k === 'PageUp') move(addMonthsISO(cursor, -12));
      else if (k === 'PageDown') move(addMonthsISO(cursor, 12));
      else if (k === 'Enter' || k === ' ') selectMonth(y, m);
      else handled = false;
    } else {
      if (k === 'ArrowLeft') move(addMonthsISO(cursor, -12));
      else if (k === 'ArrowRight') move(addMonthsISO(cursor, 12));
      else if (k === 'ArrowUp') move(addMonthsISO(cursor, -36));
      else if (k === 'ArrowDown') move(addMonthsISO(cursor, 36));
      else if (k === 'PageUp') move(addMonthsISO(cursor, -144));
      else if (k === 'PageDown') move(addMonthsISO(cursor, 144));
      else if (k === 'Enter' || k === ' ') selectYear(y);
      else handled = false;
    }
    if (handled) e.preventDefault();
  };

  const onPopupKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // 祖先（モーダル・フォーム・表の行）へキー操作を伝えない
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      if (view !== (kind === 'month' ? 'month' : 'day')) {
        // 年・月の選択中は 1 段戻る
        wantFocus.current = true;
        setView(view === 'year' ? 'month' : 'day');
        return;
      }
      onClose();
      return;
    }
    if (e.key === 'Tab') {
      // ポップアップ内でフォーカスを循環させる
      const pop = popupRef.current;
      if (!pop) return;
      const items = Array.from(pop.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex="0"]'));
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // ---- 見出し・前後ボタン
  let title: string;
  let prevLabel: string;
  let nextLabel: string;
  let step: number;
  if (view === 'day') {
    title = calendarHeading(y, m);
    prevLabel = '前の月';
    nextLabel = '次の月';
    step = 1;
  } else if (view === 'month') {
    const w = warekiYearOfYear(y);
    title = w ? `${w}（${y}年）` : `${y}年`;
    prevLabel = '前の年';
    nextLabel = '次の年';
    step = 12;
  } else {
    const start = y - (y % 12);
    title = `${start}年〜${start + 11}年`;
    prevLabel = '前の12年';
    nextLabel = '次の12年';
    step = 144;
  }

  const todayValue = kind === 'month' ? ym(today) : today;
  const todayDisabled = !isWithinRange(todayValue, min, max);
  const style: CSSProperties = pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' };

  return createPortal(
    <div
      ref={popupRef}
      id={id}
      role="dialog"
      aria-modal="false"
      aria-label={kind === 'month' ? '年月を選択' : '日付を選択'}
      tabIndex={-1}
      className={cx('bs-datepicker', pos && `is-${pos.placement}`)}
      style={style}
      onKeyDown={onPopupKeyDown}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bs-datepicker__head">
        <button type="button" className="bs-datepicker__nav" aria-label={prevLabel} title={prevLabel} onClick={() => move(addMonthsISO(cursor, -step), false)}>
          <ChevronLeft size={16} aria-hidden />
        </button>
        {view === 'year' ? (
          <span id={headingId} className="bs-datepicker__title" aria-live="polite">
            {title}
          </span>
        ) : (
          <button
            type="button"
            id={headingId}
            className="bs-datepicker__title is-button"
            aria-live="polite"
            title={view === 'day' ? '年・月を選択' : '年を選択'}
            onClick={() => {
              wantFocus.current = true;
              setView(view === 'day' ? 'month' : 'year');
            }}
          >
            {title}
            <ChevronDown size={14} aria-hidden className="bs-datepicker__caret" />
          </button>
        )}
        <button type="button" className="bs-datepicker__nav" aria-label={nextLabel} title={nextLabel} onClick={() => move(addMonthsISO(cursor, step), false)}>
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>

      {view === 'day' && (
        <table ref={gridRef} role="grid" aria-labelledby={headingId} className="bs-datepicker__grid is-days" onKeyDown={onGridKeyDown}>
          <thead>
            <tr>
              {WEEK.map((w, i) => (
                <th key={w} scope="col" abbr={`${w}曜日`} className={cx(i === 0 && 'is-sun', i === 6 && 'is-sat')}>
                  {w}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }, (_, row) => (
              <tr key={row}>
                {monthMatrix(y, m)
                  .slice(row * 7, row * 7 + 7)
                  .map((iso, col) => {
                    const disabled = isDisabledDay(iso);
                    const active = iso === cursor;
                    return (
                      <td
                        key={iso}
                        role="gridcell"
                        tabIndex={active ? 0 : -1}
                        data-active={active || undefined}
                        data-date={iso}
                        aria-label={dayLabel(iso)}
                        aria-selected={iso === value}
                        aria-current={iso === today ? 'date' : undefined}
                        aria-disabled={disabled || undefined}
                        className={cx(
                          'bs-datepicker__cell',
                          col === 0 && 'is-sun',
                          col === 6 && 'is-sat',
                          ym(iso) !== ym(cursor) && 'is-outside',
                          iso === today && 'is-today',
                          iso === value && 'is-selected',
                          disabled && 'is-disabled',
                        )}
                        onClick={() => {
                          if (disabled) return;
                          selectDay(iso);
                        }}
                      >
                        <span className="bs-datepicker__day">{splitISO(iso).d}</span>
                      </td>
                    );
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {view === 'month' && (
        <table ref={gridRef} role="grid" aria-labelledby={headingId} className="bs-datepicker__grid is-months" onKeyDown={onGridKeyDown}>
          <tbody>
            {[0, 1, 2, 3].map((row) => (
              <tr key={row}>
                {[1, 2, 3].map((c) => {
                  const mm = row * 3 + c;
                  const key = monthOf(y, mm);
                  const disabled = isDisabledMonth(y, mm);
                  const active = mm === m;
                  const selected = !!value && value.slice(0, 7) === key;
                  return (
                    <td
                      key={mm}
                      role="gridcell"
                      tabIndex={active ? 0 : -1}
                      data-active={active || undefined}
                      data-month={key}
                      aria-label={`${y}年${mm}月`}
                      aria-selected={selected}
                      aria-current={key === ym(today) ? 'date' : undefined}
                      aria-disabled={disabled || undefined}
                      className={cx('bs-datepicker__cell', key === ym(today) && 'is-today', selected && 'is-selected', disabled && 'is-disabled')}
                      onClick={() => selectMonth(y, mm)}
                    >
                      <span className="bs-datepicker__day">{mm}月</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {view === 'year' && (
        <table ref={gridRef} role="grid" aria-labelledby={headingId} className="bs-datepicker__grid is-years" onKeyDown={onGridKeyDown}>
          <tbody>
            {[0, 1, 2, 3].map((row) => (
              <tr key={row}>
                {[0, 1, 2].map((c) => {
                  const yy = y - (y % 12) + row * 3 + c;
                  const disabled = isDisabledYear(yy);
                  const active = yy === y;
                  const selected = !!value && Number(value.slice(0, 4)) === yy;
                  return (
                    <td
                      key={yy}
                      role="gridcell"
                      tabIndex={active ? 0 : -1}
                      data-active={active || undefined}
                      aria-label={`${yy}年`}
                      aria-selected={selected}
                      aria-disabled={disabled || undefined}
                      className={cx('bs-datepicker__cell', yy === Number(today.slice(0, 4)) && 'is-today', selected && 'is-selected', disabled && 'is-disabled')}
                      onClick={() => selectYear(yy)}
                    >
                      <span className="bs-datepicker__day">{yy}</span>
                      <span className="bs-datepicker__sub">{warekiYearOfYear(yy).replace(/年$/, '')}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="bs-datepicker__foot">
        <button type="button" className="bs-datepicker__action" disabled={todayDisabled} onClick={() => onSelect(todayValue)}>
          {kind === 'month' ? '今月' : '今日'}
        </button>
        <button type="button" className="bs-datepicker__action" onClick={onClear}>
          クリア
        </button>
        <span className="bs-datepicker__hint" aria-hidden>
          {kind === 'month' ? 'PgUp/PgDn：年' : 'PgUp/PgDn：月'}
        </span>
      </div>
    </div>,
    document.body,
  );
}
